import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import Markdown from "react-markdown"
import { useMutation, useQuery, useQueryClient } from "react-query"

import Button from "src/components/Button"
import CollapsibleBar from "src/components/CollapsibleBar"
import Loader from "src/components/Loader"

import {
  createNote,
  deleteNote,
  getNotes,
  updateNote,
} from "src/api/Manage/Notes"

import { formatDateRange } from "src/utils/date_helpers"

import NoteModal from "./NoteModal"

const DEFAULT_NOTES_HEIGHT = 350
const MIN_NOTES_HEIGHT = 180
const MAX_NOTES_HEIGHT = 700

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Note item for expanded view
const NoteItemExpanded = ({ note, onEdit }) => {
  const handleClick = (e) => {
    e.stopPropagation()
    onEdit(note)
  }

  const dateDisplay = formatDateRange(note.startDate, note.endDate)

  return (
    <div
      className="group cursor-pointer rounded bg-gray-100 px-4 py-3 transition-colors hover:bg-gray-200 active:bg-gray-200"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleClick(e)
        }
      }}
    >
      {/* Header with date and edit link */}
      <div className="mb-1.5 flex items-center justify-between">
        <span className="font-mono text-xs text-gray-500">{dateDisplay}</span>
        <span className="text-xs text-blue-600 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
          Edit
        </span>
      </div>

      {/* Content - render markdown */}
      <div className="text-sm leading-relaxed text-gray-900 [&_a]:text-blue-600 [&_a]:underline [&_p]:m-0">
        <Markdown>{note.text}</Markdown>
      </div>
    </div>
  )
}

NoteItemExpanded.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    text: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
}

// Main NotesBar component
const NotesBar = ({ marinaSlug, date }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [notesPanelHeight, setNotesPanelHeight] = useState(DEFAULT_NOTES_HEIGHT)
  const queryClient = useQueryClient()
  const storageKey = useMemo(
    () => `nightView.dailyNotes.height.${marinaSlug}`,
    [marinaSlug]
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    const savedHeight = window.localStorage.getItem(storageKey)
    if (!savedHeight) {
      setNotesPanelHeight(DEFAULT_NOTES_HEIGHT)
      return
    }

    const parsed = Number.parseInt(savedHeight, 10)
    if (Number.isNaN(parsed)) {
      setNotesPanelHeight(DEFAULT_NOTES_HEIGHT)
      return
    }

    setNotesPanelHeight(clamp(parsed, MIN_NOTES_HEIGHT, MAX_NOTES_HEIGHT))
  }, [storageKey])

  const {
    data: notes = [],
    isLoading,
    isError,
  } = useQuery(
    ["notes", marinaSlug, date],
    () => getNotes({ marinaSlug, date }),
    {
      refetchOnWindowFocus: false,
    }
  )

  const createMutation = useMutation(
    (data) => createNote({ marinaSlug, ...data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notes", marinaSlug, date])
        setIsModalOpen(false)
      },
    }
  )

  const updateMutation = useMutation(
    (data) => updateNote({ marinaSlug, noteId: editingNote.id, ...data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notes", marinaSlug, date])
        setEditingNote(null)
      },
    }
  )

  const deleteMutation = useMutation(
    (noteId) => deleteNote({ marinaSlug, noteId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notes", marinaSlug, date])
        setEditingNote(null)
      },
    }
  )

  const handleAdd = () => {
    setIsModalOpen(true)
  }

  const handleEdit = (note) => {
    setEditingNote(note)
  }

  const handleSave = (data) => {
    if (editingNote) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleDelete = () => {
    if (editingNote) {
      deleteMutation.mutate(editingNote.id)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingNote(null)
  }

  const handleHeightChange = (height) => {
    const nextHeight = clamp(height, MIN_NOTES_HEIGHT, MAX_NOTES_HEIGHT)
    setNotesPanelHeight(nextHeight)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, String(nextHeight))
    }
  }

  // Get first note for preview
  const firstNote = notes[0]
  const additionalCount = notes.length > 1 ? notes.length - 1 : 0

  const notePreview = isLoading ? (
    <span className="text-sm text-gray-500">Loading...</span>
  ) : isError ? (
    <span className="text-sm text-red-600">Failed to load notes</span>
  ) : notes.length === 0 ? (
    <span className="text-sm text-gray-500">No notes for today</span>
  ) : (
    <div className="flex min-w-0 items-center gap-4 overflow-hidden">
      <span className="flex min-w-0 max-w-[400px] items-center gap-2">
        <span className="font-mono flex-shrink-0 text-xs text-gray-500">
          {formatDateRange(firstNote.startDate, firstNote.endDate)}
        </span>
        <span className="truncate text-sm text-gray-600">{firstNote.text}</span>
      </span>
      {additionalCount > 0 && (
        <span className="flex-shrink-0 text-sm text-gray-500">
          +{additionalCount} more
        </span>
      )}
    </div>
  )

  return (
    <>
      <CollapsibleBar
        title="Daily Notes"
        ariaLabel="Expand daily notes"
        openStateStorageKey={`nightView.dailyNotes.open.${marinaSlug}`}
        badge={
          notes.length > 0 && (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600">
              {notes.length}
            </span>
          )
        }
        summary={notePreview}
        actions={
          <>
            <div className="sm:hidden">
              <Button
                variant="secondary"
                small
                icon="icon-plus"
                iconOnly
                title="Add note"
                onClick={handleAdd}
              />
            </div>
            <div className="hidden sm:block">
              <Button variant="secondary" small onClick={handleAdd}>
                <i className="icon icon-plus mr-1" />
                Add note
              </Button>
            </div>
          </>
        }
        resizable
        maxContentHeight={notesPanelHeight}
        minContentHeight={MIN_NOTES_HEIGHT}
        maxResizableHeight={MAX_NOTES_HEIGHT}
        onHeightChange={handleHeightChange}
        resizeHandleAriaLabel="Resize daily notes panel"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader />
          </div>
        ) : isError ? (
          <div className="py-6 text-center text-sm text-red-600">
            Failed to load notes
          </div>
        ) : notes.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">
            No notes for today
          </div>
        ) : (
          <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {notes.map((note) => (
              <NoteItemExpanded key={note.id} note={note} onEdit={handleEdit} />
            ))}
          </div>
        )}
      </CollapsibleBar>

      {/* Modal for Add/Edit */}
      {(isModalOpen || editingNote) && (
        <NoteModal
          isOpen={isModalOpen || !!editingNote}
          onClose={handleCloseModal}
          onSave={handleSave}
          onDelete={editingNote ? handleDelete : null}
          note={editingNote}
          date={date}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
          isDeleting={deleteMutation.isLoading}
        />
      )}
    </>
  )
}

NotesBar.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
}

export default NotesBar
